import React, { useState } from 'react';
import { MailPlusIcon, RefreshCwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { cancelInvitation, inviteMember, removeMember, resendInvitation, setMemberActive, updateMemberRole } from '../../services/teamService';
import { formatDate, fullName } from '../../utils/format';
import { Role, ROLE_LABELS } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Avatar } from '../../components/ui/Avatar';
import { Switch } from '../../components/ui/Switch';
import { Tag } from '../../components/ui/Tag';
const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as Role[]).map(role => ({
  value: role,
  label: ROLE_LABELS[role]
}));
const PERMISSIONS: {
  role: Role;
  text: string;
}[] = [{
  role: 'propietario',
  text: 'Acceso completo al espacio de trabajo.'
}, {
  role: 'administrador',
  text: 'Gestiona leads, formularios, landings, usuarios y configuración.'
}, {
  role: 'comercial',
  text: 'Gestiona leads y notas.'
}, {
  role: 'lectura',
  text: 'Puede consultar la información, pero no editarla.'
}];
export function Team() {
  const {
    organizationId,
    invitations
  } = useWorkspace();
  const {
    members
  } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('comercial');
  const [sending, setSending] = useState(false);
  const [toRemove, setToRemove] = useState<string | null>(null);
  const pending = invitations.filter(i => i.status === 'pendiente');
  async function handleInvite() {
    if (!email.includes('@')) return;
    setSending(true);
    await inviteMember(organizationId, email.trim(), role);
    setSending(false);
    setInviteOpen(false);
    setEmail('');
  }
  return <>
      <PageHeader title="Equipo" description="Invita a tu equipo y define qué puede hacer cada persona." actions={<Button leftIcon={<MailPlusIcon className="h-4 w-4" data-id="element-1316" data-mp="12gnfuztax95k" />} onClick={() => setInviteOpen(true)} data-id="element-1315" data-mp="xg95vc853p4w">
            Invitar usuario
          </Button>} data-id="element-1314" data-mp="tf75gs2hl71c" />

      <section className="rounded-lg border border-border bg-background shadow-sm" data-id="element-1317" data-mp="17fhyo1r9qxpo">
        <h2 className="border-b border-border px-5 py-4 text-sm font-semibold text-foreground" data-id="element-1318" data-mp="1cda0bnhpty72">
          Miembros ({members.length})
        </h2>
        <ul className="divide-y divide-border" data-id="element-1319" data-mp="1l753ipza1ek2">
          {members.map(member => <li key={member.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-id="element-1320" data-mp="1l8zk5s3imkic">
              <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-1321" data-mp="1v4j7vr7xecw0">
                <Avatar name={fullName(member.firstName, member.lastName)} data-id="element-1322" data-mp="258zq2r8vo62z" />
                <div className="min-w-0" data-id="element-1323" data-mp="2576jtd26fnvi">
                  <p className="truncate text-sm font-medium text-foreground" data-id="element-1324" data-mp="209wudxesfc7o">
                    {fullName(member.firstName, member.lastName)}
                  </p>
                  <p className="truncate text-sm text-muted-foreground" data-id="element-1325" data-mp="1r9hw7xgq62mr">
                    {member.email}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3" data-id="element-1326" data-mp="2nyz9oyly8sz0">
                <Select id={`role-${member.id}`} options={ROLE_OPTIONS} value={member.role} disabled={member.role === 'propietario'} onChange={e => updateMemberRole(member.id, e.target.value as Role)} data-id="element-1327" data-mp="2j1pcgwsbjd0m" />
                <Switch id={`active-${member.id}`} label="Acceso" checked={member.active} disabled={member.role === 'propietario'} onChange={checked => setMemberActive(member.id, checked)} data-id="element-1328" data-mp="36s37mw679rzf" />
                {member.role !== 'propietario' && <Button variant="ghost" size="sm" aria-label="Eliminar miembro" onClick={() => setToRemove(member.id)} data-id="element-1329" data-mp="2dhvq86msb1gr">
                    <Trash2Icon className="h-4 w-4" data-id="element-1330" data-mp="358jnv8p91opo" />
                  </Button>}
              </div>
            </li>)}
        </ul>
      </section>

      <section className="mt-5 rounded-lg border border-border bg-background shadow-sm" data-id="element-1331" data-mp="2wen769eut4hn">
        <h2 className="border-b border-border px-5 py-4 text-sm font-semibold text-foreground" data-id="element-1332" data-mp="30gi7bal1tbtu">
          Invitaciones pendientes ({pending.length})
        </h2>
        {pending.length === 0 ? <p className="p-5 text-sm text-muted-foreground" data-id="element-1333" data-mp="3sxujycvnmuxh">
            No hay invitaciones pendientes.
          </p> : <ul className="divide-y divide-border" data-id="element-1334" data-mp="1hh14c5f39c59">
            {pending.map(invitation => <li key={invitation.id} className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-1335" data-mp="1cizkvjdr3at9">
                <div data-id="element-1336" data-mp="1m929z8hr44mb">
                  <p className="text-sm font-medium text-foreground" data-id="element-1337" data-mp="1man4wz7z9pkp">
                    {invitation.email}
                  </p>
                  <p className="text-xs text-muted-foreground" data-id="element-1338" data-mp="ylu8z2rcvhq3">
                    {ROLE_LABELS[invitation.role]} · enviada el{' '}
                    {formatDate(invitation.createdAt)}
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-1339" data-mp="13ib5olcmf4es">
                  <Button variant="secondary" size="sm" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-1341" data-mp="2jv9ginxm871b" />} onClick={() => resendInvitation(invitation.id)} data-id="element-1340" data-mp="13h9k3mxzazit">
                    Reenviar
                  </Button>
                  <Button variant="ghost" size="sm" leftIcon={<XIcon className="h-4 w-4" data-id="element-1343" data-mp="2r2djm2r03mvo" />} onClick={() => cancelInvitation(invitation.id)} data-id="element-1342" data-mp="2m0y04f4h1ofe">
                    Cancelar
                  </Button>
                </div>
              </li>)}
          </ul>}
      </section>

      <section className="mt-5 rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1344" data-mp="23c9hy0xdhx7b">
        <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1345" data-mp="28aayae3ezif3">Permisos por rol</h2>
        <ul className="grid gap-3 sm:grid-cols-2" data-id="element-1346" data-mp="2d843b3sze6ds">
          {PERMISSIONS.map(permission => <li key={permission.role} className="rounded-lg border border-border bg-surface p-4" data-id="element-1347" data-mp="2i0e279c2qgs7">
              <Tag data-id="element-1348" data-mp="1kidyxmh5iiuv">{ROLE_LABELS[permission.role]}</Tag>
              <p className="mt-2 text-sm text-muted-foreground" data-id="element-1349" data-mp="1pi9v4zjlx5zv">{permission.text}</p>
            </li>)}
        </ul>
      </section>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invitar a un usuario" footer={<div className="flex justify-end gap-2" data-id="element-1351" data-mp="3ts3mnvnlm6be">
            <Button variant="ghost" onClick={() => setInviteOpen(false)} data-id="element-1352" data-mp="3tuzi9dbz4iwo">
              Cancelar
            </Button>
            <Button onClick={handleInvite} loading={sending} data-id="element-1353" data-mp="3jup6zy9i1lk5">
              Enviar invitación
            </Button>
          </div>} data-id="element-1350" data-mp="6luilvddpat0">
        <div className="space-y-4" data-id="element-1354" data-mp="3f373p3k078w0">
          <Input id="invite-email" label="Email" type="email" placeholder="nombre@empresa.com" value={email} onChange={e => setEmail(e.target.value)} data-id="element-1355" data-mp="3f1w47fxlcnap" />
          <Select id="invite-role" label="Rol" options={ROLE_OPTIONS.filter(r => r.value !== 'propietario')} value={role} onChange={e => setRole(e.target.value as Role)} data-id="element-1356" data-mp="314qxretytmex" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toRemove)} title="Eliminar miembro" description="Perderá el acceso a este espacio de trabajo inmediatamente." onCancel={() => setToRemove(null)} onConfirm={async () => {
      if (toRemove) await removeMember(toRemove);
      setToRemove(null);
    }} data-id="element-1357" data-mp="svj5387fnuyt" />
    </>;
}